import React, { useEffect, useState } from 'react';
import { Workflow, Plus, X, Loader2, MousePointerClick, List as ListIcon, Square } from 'lucide-react';
import { crmClient, unwrap } from '../../analytics-shared/api/client';

/**
 * ScreenFlowsPanel — builder advanced setting that attaches Screen Flows
 * to a report. Attached flows become actions on the RUN page (saved
 * reports only):
 *
 *   basic          launches with no record context
 *   record_detail  user picks ONE record → recordId variable
 *   list_view      user picks records (or all) → recordIds / Flow.selectedRecordIds
 *
 * Persisted on the config as:
 *   screenFlows: [{flowId, name, launchMode, flowObject}]
 */

const MODE_META = {
  basic: { label: 'Default', icon: Square, hint: 'Runs standalone — no record context' },
  record_detail: { label: 'Record', icon: MousePointerClick, hint: 'Runs with one selected record (recordId)' },
  list_view: { label: 'List view', icon: ListIcon, hint: 'Runs with selected records (recordIds)' },
};

export const modeMeta = (mode) => MODE_META[mode] || MODE_META.basic;

const ScreenFlowsPanel = ({ config, onChange }) => {
  const [flows, setFlows] = useState(null);
  const [error, setError] = useState(null);
  const attached = config.screenFlows || [];

  useEffect(() => {
    let cancelled = false;
    crmClient.get('/flow-builder/flows', { params: { status: 'active', limit: 100 } })
      .then(unwrap)
      .then((res) => {
        if (cancelled) return;
        const list = (res?.flows || []).filter((f) => f.flow_type === 'screen');
        setFlows(list);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message || 'Failed to load flows'); setFlows([]); } });
    return () => { cancelled = true; };
  }, []);

  const attach = (f) => {
    if (attached.some((a) => a.flowId === f.id)) return;
    onChange([...attached, {
      flowId: f.id,
      name: f.name,
      launchMode: f.launch_mode || 'basic',
      flowObject: f.screen_flow_object || null,
    }]);
  };
  const detach = (flowId) => onChange(attached.filter((a) => a.flowId !== flowId));

  const available = (flows || []).filter((f) => !attached.some((a) => a.flowId === f.id));

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Workflow className="h-4 w-4 text-indigo-600" /> Screen Flows
          {attached.length > 0 && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 rounded-full">{attached.length}</span>}
        </h3>
        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
          Attached flows appear as actions on the report's run page after you save.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {attached.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Attached</div>
            <div className="space-y-1.5">
              {attached.map((a) => {
                const meta = modeMeta(a.launchMode);
                const Icon = meta.icon;
                return (
                  <div key={a.flowId} className="flex items-center gap-2 bg-white border border-indigo-200 rounded-lg px-2.5 py-2">
                    <Icon className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800 truncate">{a.name}</div>
                      <div className="text-[10px] text-slate-400">
                        {meta.label}{a.flowObject ? ` · ${a.flowObject}` : ''}
                      </div>
                    </div>
                    <button onClick={() => detach(a.flowId)} className="text-slate-300 hover:text-red-500 p-1">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Available screen flows</div>
          {flows === null && (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading flows…
            </div>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
          {flows !== null && !error && available.length === 0 && (
            <p className="text-xs text-slate-400 py-2">
              {flows.length === 0
                ? 'No active screen flows yet — build one in Flow Builder.'
                : 'All active screen flows are attached.'}
            </p>
          )}
          <div className="space-y-1.5">
            {available.map((f) => {
              const meta = modeMeta(f.launch_mode);
              const Icon = meta.icon;
              const objectMismatch = f.screen_flow_object
                && f.launch_mode !== 'basic'
                && f.screen_flow_object.toLowerCase() !== (config.object || '').toLowerCase();
              return (
                <button key={f.id} onClick={() => attach(f)}
                  className="w-full flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-left hover:border-indigo-300 transition-colors">
                  <Icon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-800 truncate">{f.name}</div>
                    <div className="text-[10px] text-slate-400" title={meta.hint}>
                      {meta.label}{f.screen_flow_object ? ` · ${f.screen_flow_object}` : ''}
                      {objectMismatch && <span className="text-amber-500"> · built for a different object</span>}
                    </div>
                  </div>
                  <Plus className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenFlowsPanel;
